import React, { useState, memo, useMemo, useCallback } from 'react';
import { RoomIssuesExportColumnModal } from './ExportColumnModal';
import { Stack, Link, Typography, SelectChangeEvent } from '@mui/material';
import { IOptions, RoomLevelIssues } from 'types/room';
import { PagingInfo } from 'types/paging';
import { TextEllipsis } from 'app/components/elements/TextEllipsis';
import { HeaderValidation } from '../../elements/HeaderValidation';
import { TableCustom } from '../../../../../components/elements/TableCustom';
import { HEADER_ROOM_LEVEL_ISSUES_TAB } from 'const/room';
import { styled } from '@mui/material/styles';
import { getParentNameByLevel } from '../../../../ReportPage';
import dayjs from 'dayjs';
import { camelCase, lowerCase, startCase, uniqBy } from 'lodash';
import { ListOptions } from 'app/pages/RoomReportViewPhotoPage/hook';
import { useAuth } from 'contexts/auth';
import { Role } from 'const/common';
import { InputSelect } from '../../../../../components/elements/InputSelect';
import { SearchType } from 'types/loctions';
import { convertFileToStorage } from 'utils/convertData';
// import { RoomLevelIssuePhotos } from 'types/room';
import { ModalCreateRoomLevelIssue } from '../../../../../components/RoomPage/ModalRoomLevelIssues';
import { PhotoPreviewGroup } from '../../../../../components/elements/PhotoPreviewThumbnail';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useFlash } from 'contexts/flash';
import { updateRoomLevelIssue } from 'services/startInspection';
import { deleteRoomIssue } from 'services/inspections';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
import { ButtonCustom } from '../../../../../components/elements/ButtonCustom';
import { FilterField } from '../../../../../components/elements/FilterBar';
import { CriticalityChip } from 'app/pages/IssuesPage/components/CriticalityChip';
import { getAdHocDueDateColor, IssuesUiConfig } from 'utils/issueDueDateColor';
import { getCriticalityDisplay } from 'utils/issueCriticality';
import { DEFAULT_CRITICALITY_LEVELS } from 'utils/issueCriticality';

type RenderDataRoomLevelProps = {
  dataRoomLevelIssues: RoomLevelIssues[];
  assignmentMode?: boolean;
  issuesConfig?: IssuesUiConfig;
  navigateDetail?: (data: RoomLevelIssues) => void;
  onOpenAssignment?: (data: RoomLevelIssues) => void;
  // onViewPhotos?: (photos: RoomLevelIssuePhotos[]) => void;
  keySearch?: { key: string; value: string; label: string; level?: number }[];
  onClickRoom: (value?: { key: string; value: string; label: string; level?: number }) => void;
  optionsLargestInTable: IOptions[];
  getLabelField: (type: string) => string;
  isCanAction: boolean;
  optionValue: string;
  setOptionValue: React.Dispatch<React.SetStateAction<string>>;
};

const renderDataRoomLevelIssues = ({
  dataRoomLevelIssues,
  assignmentMode,
  issuesConfig,
  keySearch,
  optionsLargestInTable,
  isCanAction,
  getLabelField,
  onClickRoom,
  navigateDetail,
  onOpenAssignment,
  // onViewPhotos,
  optionValue,
  setOptionValue
}: RenderDataRoomLevelProps) => {
  const criticalityLevels = issuesConfig?.criticalityLevels?.length
    ? issuesConfig.criticalityLevels
    : DEFAULT_CRITICALITY_LEVELS;
  const formatActionByDisplay = (actionBy: string | undefined) => {
    const raw = actionBy?.trim();
    if (!raw) return '';
    if (raw.startsWith('other_')) return raw.slice('other_'.length);
    return raw;
  };
  const checkIsFilter = (key: string) => {
    return !!keySearch?.find((item) => item.key === key);
  };
  return dataRoomLevelIssues.map((issue) => {
    const roomLevelIssuesOptions: Record<string, { value: JSX.Element }> = {};
    optionsLargestInTable.forEach((issueOption) => {
      roomLevelIssuesOptions[`${camelCase(getLabelField(issueOption?.type))}`] = {
        value: <></>
      };
    });
    issue.options.forEach((issueOption) => {
      roomLevelIssuesOptions[`${camelCase(getLabelField(issueOption?.type))}`] = {
        value: (
          <TextEllipsis
            onClick={() => {
              setOptionValue(issueOption.value);
              onClickRoom({
                value: issueOption.value,
                label: issueOption.value,
                key: 'optionValue'
              });
            }}
            text={issueOption?.value}
            width={'95%'}
            active={checkIsFilter(`optionValue`) && optionValue === issueOption.value}
          />
        )
      };
    });
    return {
      clientName: {
        value: (
          <TextEllipsis
            onClick={() => {
              onClickRoom({
                value: issue?.room.client.id.toString(),
                label: issue?.room?.client?.clientName,
                key: 'clientId'
              });
            }}
            text={issue?.room?.client?.clientName}
            width={'95%'}
            active={checkIsFilter('clientId')}
          />
        )
      },
      mainLocation: {
        value: (
          <TextEllipsis
            onClick={() => {
              onClickRoom({
                value: getParentNameByLevel(issue.room, 1)?.id?.toString() || '',
                label: getParentNameByLevel(issue.room, 1)?.name || '',
                key: 'locationId',
                level: 1
              });
            }}
            text={getParentNameByLevel(issue.room, 1)?.name || ''}
            width={'95%'}
            active={!!keySearch?.find((item) => item.key === 'locationId' && item.level === 1)}
          />
        )
      },
      subLocation1: {
        value: (
          <TextEllipsis
            onClick={() => {
              onClickRoom({
                value: getParentNameByLevel(issue.room, 2)?.id?.toString() || '',
                label: getParentNameByLevel(issue.room, 2)?.name || '',
                key: 'locationId',
                level: 2
              });
            }}
            text={getParentNameByLevel(issue.room, 2)?.name || ''}
            width={'95%'}
            active={!!keySearch?.find((item) => item.key === 'locationId' && item.level === 2)}
          />
        )
      },
      subLocation2: {
        value: (
          <TextEllipsis
            onClick={() => {
              const val = getParentNameByLevel(issue.room, 3);
              if (!val?.id) return;
              onClickRoom({
                value: val.id.toString(),
                label: val.name || '',
                key: 'locationId',
                level: 3
              });
            }}
            text={getParentNameByLevel(issue.room, 3)?.name || ''}
            width={'95%'}
            active={!!keySearch?.find((item) => item.key === 'locationId' && item.level === 3)}
          />
        )
      },
      verticalLevel: {
        value: (
          <TextEllipsis
            onClick={() => {
              onClickRoom({
                value: issue?.room.parent?.id?.toString(),
                label: issue?.room.parent?.name,
                key: 'locationId',
                level: 4
              });
            }}
            text={getParentNameByLevel(issue.room, 4)?.name || ''}
            width={'95%'}
            active={!!keySearch?.find((item) => item.key === 'locationId' && item.level === 4)}
          />
        )
      },
      location: {
        value: (
          <TextEllipsis
            onClick={() => {
              onClickRoom({
                value: issue?.room.id.toString(),
                label: issue?.room.name,
                key: 'locationId',
                level: 5
              });
            }}
            text={getParentNameByLevel(issue.room, 5)?.name || ''}
            width={'95%'}
            active={!!keySearch?.find((item) => item.key === 'locationId' && item.level === 5)}
          />
        )
      },
      type: {
        value: (
          <TextEllipsis
            onClick={() => {
              onClickRoom({
                value: issue.type,
                label: issue.type,
                key: 'type'
              });
            }}
            text={issue.type}
            width={'95%'}
            active={checkIsFilter('type')}
          />
        )
      },
      description: {
        value: (
          <TextEllipsis
            onClick={() => {
              onClickRoom({
                value: issue.description,
                label: issue.description,
                key: 'description'
              });
            }}
            text={issue.description}
            width={'95%'}
            active={checkIsFilter('description')}
          />
        )
      },
      validated: {
        value: (
          <TextEllipsis
            onClick={() => {
              onClickRoom({
                value: issue.validated ? '1' : '0',
                label: issue.validated ? 'Yes' : 'No',
                key: 'validated'
              });
            }}
            text={issue.validated ? 'Yes' : 'No'}
            width={'95%'}
            active={checkIsFilter('validated')}
          />
        )
      },
      ...roomLevelIssuesOptions,
      actionBy: {
        value: (
          <TextEllipsis
            onClick={() => {
              onClickRoom({
                value: issue.actionBy,
                label: issue.actionBy,
                key: 'actionBy'
              });
            }}
            text={formatActionByDisplay(issue.actionBy)}
            width={'95%'}
            active={checkIsFilter('actionBy')}
          />
        )
      },
      actionByDate: {
        value: (
          <TextEllipsis
            onClick={() => {
              onClickRoom({
                value: issue.actionByDate,
                label: issue.actionByDate,
                key: 'actionByDate'
              });
            }}
            text={issue.actionByDate ? dayjs(issue.actionByDate).format('YYYY-MM-DD') : ''}
            width={'95%'}
            active={checkIsFilter('actionByDate')}
          />
        )
      },
      ...(assignmentMode
        ? {
            assignedBy: {
              value: <TextEllipsis text={issue.assignedByUser?.name || '-'} width={'95%'} />
            },
            assignedTo: {
              value: (
                <TextEllipsis
                  text={issue.assignedToUser?.name || formatActionByDisplay(issue.actionBy) || '-'}
                  width={'95%'}
                />
              )
            },
            dueDate: {
              value: (() => {
                const due = issue.dueDate || issue.actionByDate;
                if (!due) {
                  return (
                    <Typography fontSize={12} color="text.secondary">
                      -
                    </Typography>
                  );
                }
                return (
                  <Typography color={getAdHocDueDateColor(due, issue.status, issuesConfig)}>
                    {dayjs(due).format('YYYY-MM-DD')}
                  </Typography>
                );
              })()
            },
            criticality: {
              value: (
                <CriticalityChip
                  criticality={
                    issue.criticality ||
                    getCriticalityDisplay(issue.criticalityKey, criticalityLevels)
                  }
                />
              )
            }
          }
        : {}),
      status: {
        value: (
          <TextEllipsis
            onClick={() => {
              onClickRoom({
                value: issue.status,
                label: issue.status,
                key: 'status'
              });
            }}
            text={issue.status}
            width={'95%'}
            active={checkIsFilter('status')}
          />
        )
      },
      dateResolved: {
        value: (
          <TextEllipsis
            text={
              issue.resolutionDate ? dayjs(issue.resolutionDate).format('YYYY-MM-DD HH:mm') : ''
            }
            width={'95%'}
          />
        )
      },
      dateCreated: {
        value: (
          <TextEllipsis text={dayjs(issue.createdAt).format('YYYY-MM-DD HH:mm')} width={'95%'} />
        )
      },
      lastModified: {
        value: (
          <TextEllipsis text={dayjs(issue.updatedAt).format('YYYY-MM-DD HH:mm')} width={'95%'} />
        )
      },
      photo: {
        value: (
          <Stack flexDirection={'row'} gap={1.25} alignItems={'flex-start'}>
            <Stack flexDirection={'row'} gap={3}>
              {issue.roomLevelIssuePhotos?.length > 0 ? (
                <PhotoPreviewGroup
                  photos={issue.roomLevelIssuePhotos.map((p, i) => ({
                    src: p.url || convertFileToStorage('inspections', p.photo),
                    alt: `issue-photo-${i}`,
                    key: p.id ?? i
                  }))}
                  thumbnailWidth={50}
                  thumbnailHeight={50}
                />
              ) : (
                <div style={{ width: 50, height: 50 }} />
              )}
              {isCanAction && (
                <Stack gap={1}>
                  <Link
                    style={{ fontWeight: 600, cursor: 'pointer' }}
                    onClick={() => {
                      if (onOpenAssignment) {
                        onOpenAssignment(issue);
                        return;
                      }
                      navigateDetail && navigateDetail(issue);
                    }}>
                    {onOpenAssignment ? 'Edit assignment' : 'View / Edit'}
                  </Link>
                </Stack>
              )}
            </Stack>
          </Stack>
        )
      }
    };
  });
};

export type RoomLevelIssuesTabProps = {
  assignmentMode?: boolean;
  issuesConfig?: IssuesUiConfig;
  onOpenAssignment?: (issue: RoomLevelIssues) => void;
  dataRoomLevelIssues: RoomLevelIssues[];
  pagingInfo: PagingInfo;
  handleChangePage: (value: number) => void;
  handleSort: (orderBy: string) => void;
  sortOption: { field?: string; order?: string };
  onSearch?: (value: string) => void;
  initSearchText?: string;
  filterRoomIssues?: { key: string; value: string; label: string; level?: number }[];
  options: ListOptions;
  searchType: string;
  setSearchType: (value: SearchType) => void;
  archiveFilter?: 'active' | 'archived';
  setArchiveFilter?: (value: 'active' | 'archived') => void;
  setFilterRoomIssues: (value?: {
    key: string;
    value: string;
    label: string;
    level?: number;
  }) => void;
  refreshDataRoomLevelIssues: () => void;
  onExport: (
    type: string,
    imageOption: string,
    columns: string[],
    dropdownOptionColumns?: number[],
    singleFieldColumns?: number[]
  ) => Promise<void>;
};

const optionSearchType = [
  { label: 'Partial Text', value: 'partial-text' },
  { label: 'Full text', value: 'full-text' }
];

export const RoomLevelIssuesTab = ({
  assignmentMode,
  issuesConfig,
  onOpenAssignment,
  dataRoomLevelIssues,
  pagingInfo,
  searchType,
  setSearchType,
  archiveFilter = 'active',
  setArchiveFilter,
  handleChangePage,
  handleSort,
  sortOption,
  onSearch,
  initSearchText,
  setFilterRoomIssues,
  filterRoomIssues,
  options,
  refreshDataRoomLevelIssues,
  onExport
}: RoomLevelIssuesTabProps) => {
  const [dataSelect, setDataSelect] = useState<RoomLevelIssues | null>(null);
  const [openRoomLevelIssues, setOpenRoomLevelIssues] = useState<boolean>(false);
  const [openExportModal, setOpenExportModal] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>(initSearchText || '');
  const [optionValue, setOptionValue] = useState<string>('');

  const { singleEntryField } = useFilteredRoomContext();
  const { setFlash } = useFlash();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCreateIssue = async (payload: any) => {
    if (payload?.id && dataSelect?.submitId) {
      await updateRoomLevelIssue(payload, dataSelect.submitId);
      refreshDataRoomLevelIssues();
      setFlash({ type: 'success', message: 'Update successful' });
      setOpenRoomLevelIssues(false);
      return;
    }
  };

  const { asyncCallback } = useAsyncCallback(handleCreateIssue, [
    dataSelect,
    refreshDataRoomLevelIssues
  ]);

  const handleDeleteFromUpdateModal = useCallback(
    async ({ id, submitId }: { id: number; submitId: number }) => {
      try {
        await deleteRoomIssue({
          submitId: String(submitId),
          roomIssuesId: String(id)
        });
        refreshDataRoomLevelIssues();
        setFlash({
          type: 'success',
          message: 'Location level issue removed.'
        });
        setDataSelect(null);
        setOpenRoomLevelIssues(false);
      } catch {
        setFlash({ type: 'error', message: 'Could not delete this location level issue.' });
        throw new Error('delete failed');
      }
    },
    [refreshDataRoomLevelIssues, setFlash]
  );

  const { auth } = useAuth();
  const isCanAction = useMemo(
    () => (auth?.role ? [Role.ADMIN, Role.SUPER_ADMIN].includes(auth?.role) : false),
    [auth?.role]
  );

  const mapOptions = useMemo(() => {
    const mapOptions = new Map();
    options?.forEach((op) => {
      mapOptions.set(op.id.toString(), op);
    });
    return mapOptions;
  }, [options]);

  const tableRoomLevelIssueHeaders = useMemo(() => {
    const tableRoomLevelIssueHeaders = [...HEADER_ROOM_LEVEL_ISSUES_TAB];
    if (assignmentMode) {
      const statusIdx = tableRoomLevelIssueHeaders.findIndex((h) => h.label === 'Status');
      const insertAt = statusIdx >= 0 ? statusIdx : tableRoomLevelIssueHeaders.length - 3;
      tableRoomLevelIssueHeaders.splice(
        insertAt,
        0,
        { label: 'Assigned By', sort: 'assignedByUser.name', width: 140 },
        { label: 'Assigned To', sort: 'assignedToUser.name', width: 140 },
        { label: 'Due Date', sort: 'dueDate', width: 130 },
        { label: 'Criticality', width: 120 }
      );
    }
    let roomLevelIssuesLargestOptions: IOptions[] = [];
    dataRoomLevelIssues?.forEach((issue) => {
      roomLevelIssuesLargestOptions = [...roomLevelIssuesLargestOptions, ...issue.options];
      roomLevelIssuesLargestOptions = uniqBy(roomLevelIssuesLargestOptions, 'type');
    });
    const totalRow = tableRoomLevelIssueHeaders.length - 5;
    roomLevelIssuesLargestOptions?.forEach((reportOption, index) => {
      tableRoomLevelIssueHeaders.splice(totalRow + index, 0, {
        label: startCase(
          lowerCase(
            mapOptions.get(reportOption?.type)?.name ||
              singleEntryField?.find((item) => item?.id.toString() === String(reportOption?.type))
                ?.name ||
              reportOption?.type
          )
        ),
        width: 200
      });
    });
    const headers = tableRoomLevelIssueHeaders.map((header) => ({
      ...header,
      key: camelCase(header.label.toLowerCase())
    }));
    return { headers, roomLevelIssuesLargestOptions };
  }, [assignmentMode, dataRoomLevelIssues, mapOptions, singleEntryField]);

  return (
    <WrapperTab>
      {onSearch && (
        <>
          <HeaderValidation
            searchText={searchText}
            setSearchText={setSearchText}
            onSearch={() => onSearch(searchText)}
          />
          <Stack
            style={{
              flexDirection: 'row',
              alignItems: 'flex-end',
              flexWrap: 'wrap',
              gap: '24px'
            }}>
            <FilterField minWidth={180}>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#226AAA',
                  whiteSpace: 'nowrap',
                  mb: '10px'
                }}>
                Search Type
              </Typography>
              <InputSelect
                value={searchType}
                onChange={(e) => setSearchType(e.target.value as SearchType)}
                options={optionSearchType}
                sx={{ width: '100%' }}
              />
            </FilterField>
            {setArchiveFilter && (
              <InputSelect
                value={archiveFilter}
                placeholder={'Archive'}
                title={'Archive'}
                colorTitle="#226aaa"
                onChange={(e: SelectChangeEvent<unknown>) => {
                  setArchiveFilter(e.target.value as 'active' | 'archived');
                }}
                sx={{ minHeight: '46px', height: '46px', padding: 0 }}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'archived', label: 'Archive' }
                ]}
              />
            )}
            <ButtonCustom
              sx={{ width: 'max-content', height: '56px', padding: '0 32px' }}
              label={'Export'}
              onClick={() => {
                setOpenExportModal(true);
              }}
            />
          </Stack>
        </>
      )}

      <Stack sx={{ marginTop: '30px', marginBottom: '30px' }}>
        {dataRoomLevelIssues && (
          <TableCustom
            isScrollX
            data={renderDataRoomLevelIssues({
              optionValue,
              setOptionValue,
              dataRoomLevelIssues,
              assignmentMode,
              issuesConfig,
              keySearch: filterRoomIssues,
              isCanAction,
              optionsLargestInTable: tableRoomLevelIssueHeaders.roomLevelIssuesLargestOptions,
              getLabelField: (type) => mapOptions.get(type)?.name || type,
              onOpenAssignment,
              navigateDetail: (data: RoomLevelIssues) => {
                setDataSelect(data);
                setOpenRoomLevelIssues(true);
              },
              // onViewPhotos: undefined,
              onClickRoom: (value?: {
                key: string;
                value: string;
                label: string;
                level?: number;
              }) => {
                setFilterRoomIssues(value);
              }
            })}
            headers={tableRoomLevelIssueHeaders.headers}
            handleChooseRow={() => {}}
            onSortHeader={handleSort}
            sort={sortOption}
            styledTableRow={{ cursor: 'default' }}
            pagingInfo={pagingInfo}
            handleChangePage={handleChangePage}
          />
        )}
      </Stack>

      <RoomIssuesExportColumnModal
        open={openExportModal}
        onClose={() => setOpenExportModal(false)}
        onExport={onExport}
      />
      <ModalCreateRoomLevelIssue
        onCreate={asyncCallback}
        initValue={dataSelect}
        onDeleteIssue={isCanAction ? handleDeleteFromUpdateModal : undefined}
        onClose={() => {
          setOpenRoomLevelIssues(false);
          setDataSelect(null);
        }}
        open={openRoomLevelIssues}
      />
    </WrapperTab>
  );
};

export const MemoRoomLevelIssuesTab = memo(RoomLevelIssuesTab);

const WrapperTab = styled(Stack)(() => ({
  width: '100%',
  marginTop: 20
}));
